// Runs at most `maxConcurrent` tasks at once and queues the rest in call order. A finishing task
// hands its slot straight to the next queued task, so a new caller can never jump the queue.
export function createConcurrencyLimiter(maxConcurrent: number) {
  let activeTaskCount = 0;
  const queuedTaskStarters: Array<() => void> = [];

  const acquireSlot = async (): Promise<void> => {
    if (activeTaskCount < maxConcurrent) {
      activeTaskCount += 1;
      return;
    }
    await new Promise<void>((resolve) => queuedTaskStarters.push(resolve));
  };

  const releaseSlot = () => {
    const startNextTask = queuedTaskStarters.shift();
    if (startNextTask) {
      startNextTask();
    } else {
      activeTaskCount -= 1;
    }
  };

  // A task cancelled while queued gives its slot straight back instead of running, so an abandoned
  // page's backlog drains immediately.
  return async function runLimited<Result>(task: () => Promise<Result>, signal?: AbortSignal): Promise<Result> {
    await acquireSlot();
    if (signal?.aborted) {
      releaseSlot();
      throw new DOMException('The task was cancelled before it started.', 'AbortError');
    }
    try {
      return await task();
    } finally {
      releaseSlot();
    }
  };
}
